import { useEffect, type ReactNode } from 'react';
import { Link } from '../lib/router';
import { fetchMatch, flush, useCanEdit, usePendingCount, useStore } from '../lib/store';

export function ScreenHeader({ title, step, left, right }: { title: string; step?: string; left?: ReactNode; right?: ReactNode }) {
  return (
    <header className="flex items-center gap-3 pt-[max(1rem,env(safe-area-inset-top))] pb-4">
      {left}
      <div className="min-w-0 flex-1">
        {step && <p className="eyebrow text-gold-soft">Step {step}</p>}
        <h1 className="font-display text-[1.75rem] leading-tight font-extrabold text-balance">{title}</h1>
      </div>
      {right}
    </header>
  );
}

export function BackButton({ href, label = 'Back' }: { href: string; label?: string }) {
  return (
    <Link href={href} className="btn btn-ghost h-11 w-11 shrink-0 rounded-full p-0" aria-label={label}>
      ←
    </Link>
  );
}

/** "Saved" / "Saving…" / "Offline · 2 queued" indicator. */
export function SyncBadge({ code }: { code: string }) {
  const pending = usePendingCount(code);
  const online = useStore((s) => s.online);
  const syncing = useStore((s) => s.syncing);
  const canEdit = useCanEdit(code);
  if (!canEdit) {
    return <span className="rounded-full bg-white/10 px-2.5 py-1 text-xs font-semibold text-cream/80">View only</span>;
  }
  const [text, cls] = !online
    ? [`Offline · ${pending} queued`, 'bg-amber-200 text-amber-950']
    : pending > 0
      ? [syncing ? 'Saving…' : `Retrying · ${pending}`, 'bg-white/15 text-cream']
      : ['Saved ✓', 'bg-success-700/70 text-cream/95'];
  return (
    <button
      type="button"
      onClick={() => void flush()}
      className={`rounded-full px-2.5 py-1 text-xs font-semibold ${cls}`}
      aria-live="polite"
      title={pending > 0 ? 'Tap to retry now' : 'All changes saved'}
    >
      {text}
    </button>
  );
}

/** Loads a match (cached first, then network) and exposes it with edit rights. */
export function useMatchData(code: string) {
  const data = useStore((s) => s.matches[code]);
  const error = useStore((s) => s.errors[code]);
  const canEdit = useCanEdit(code);
  useEffect(() => {
    void fetchMatch(code);
  }, [code]);
  return { data, error, canEdit };
}

export function NotFound({ message }: { message: string }) {
  return (
    <main className="mx-auto grid max-w-md place-items-center gap-4 px-4 py-24 text-center">
      <p className="text-6xl" aria-hidden>
        🂠
      </p>
      <h1 className="font-display text-2xl font-bold">{message}</h1>
      <Link href="/home" className="btn btn-primary">
        Back home
      </Link>
    </main>
  );
}
